'use server'

import { trFold } from '@/lib/lesson-chat'
import { createClient } from '@/lib/supabase/server'
import { createAdminClient } from '@/lib/supabase/admin'
import {
  getOpeningMessage,
  getQuestionAnswer,
  getTutorReply,
  type ChatHistoryItem,
  type CourseLevel,
  type GoalContext,
  type LessonChatContext,
  type OpeningMessage,
  type QaHistoryItem,
  type SceneContext,
  type FreeTasks,
  type FreeTopic,
  type FreeTopicPlan,
  type TutorQaAnswer,
  getMessageCheck,
  type MessageCheck,
  getCustomScene,
  type CustomScenePhrase,
} from '@/lib/deepseek'
import { DEFAULT_PERSONA, isPersona, personaOrDefault, type Persona } from '@/lib/personas'
import type { LessonContent, RoleScene } from '@/lib/lessons/types'
import { isRoleScene, SCENES } from '@/lib/travel'
import { sceneGate } from '@/lib/scene-access'
import { CUSTOM_SCENE_PROMPT_MAX, normalizeSuggestions, type AiUsage, type ChatMessage } from '@/lib/chat'
import { pickScenarioTaskWords, usedTaskWords } from '@/lib/lesson-chat'
import {
  lessonContextFrom,
  LESSON_OPENING_VARIANTS,
  lessonOpeningBase,
  OPENING_VARIANTS,
  qaCacheKey,
  sceneContextFrom,
  sceneOpeningBase,
} from '@/lib/ai-cache'
import { afterOneMore, deviceId, getAiUsage, recordAiUsage, usageExhausted } from '@/lib/ai-usage'
import { goalById } from '@/lib/goals'
import { passedRules } from '@/lib/grammar/line'
import { passedGrammarNames } from '@/lib/grammar/chat-grammar'
import { topicById } from '@/lib/grammar/topics'

type SupabaseClient = Awaited<ReturnType<typeof createClient>>

const LEVEL_ORDER: CourseLevel[] = ['A1', 'A2', 'B1']

// Режим новой сессии: сцена «к отпуску» / «для жизни», разговор по уроку,
// разговор с грамматической целью (.scratch/grammar-trainer/06), финальная
// сцена цели, «своя ситуация» (.scratch/custom-scene/01) или обычный.
// persona — характер собеседника (lib/personas.ts), только у сцен.
type SessionMode = {
  scene: RoleScene | null
  lessonSlug: string | null
  grammarGoal: string | null
  learningGoal: string | null
  customSceneId: string | null
  persona: Persona | null
}

// Параметры запроса от клиента одним объектом — чтобы не плодить позиционные
// аргументы у startConversation / sendChatMessage.
export type ChatModeRequest = {
  scene?: string | null
  lesson?: string | null
  goal?: string | null
  skill?: string | null
  custom?: string | null
  persona?: string | null
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

function parseMode(req: ChatModeRequest): SessionMode {
  const none: SessionMode = { scene: null, lessonSlug: null, grammarGoal: null, learningGoal: null, customSceneId: null, persona: null }
  const persona = isPersona(req.persona) ? req.persona : null
  if (isRoleScene(req.scene)) return { ...none, scene: req.scene, persona }
  if (req.custom && UUID_RE.test(req.custom)) return { ...none, customSceneId: req.custom, persona }
  if (req.lesson && /^[a-z0-9-]{1,80}$/.test(req.lesson)) return { ...none, lessonSlug: req.lesson }
  if (topicById(req.goal)?.chatGoal) return { ...none, grammarGoal: req.goal ?? null }
  // Финальная сцена цели «Моя цель» (lib/goals.ts): ?skill=<id цели>.
  if (goalById(req.skill)) return { ...none, learningGoal: req.skill ?? null }
  return none
}

// Обычный разговор: без сцены, урока, грамматической цели, цели и своей ситуации.
function isFreeChat(info: { scene: unknown; lessonSlug: unknown; grammarGoal: unknown; learningGoal: unknown; customSceneId: unknown }): boolean {
  return !info.scene && !info.lessonSlug && !info.grammarGoal && !info.learningGoal && !info.customSceneId
}

// Тема обычного разговора - последний пройденный урок курса: название юнита и
// русские реплики короткого диалога. Без пройденных уроков - null (знакомство).
async function loadFreeTopic(supabase: SupabaseClient, userId: string): Promise<FreeTopic> {
  const { data } = await supabase
    .from('user_lesson_progress')
    .select('lessons!inner(slug, sort_order, unit_title, track)')
    .eq('user_id', userId)
    .eq('lessons.track', 'course')
    .not('completed_at', 'is', null)
  const rows = ((data ?? []) as unknown as { lessons: { slug: string; sort_order: number; unit_title: string | null } }[]).map((r) => r.lessons)
  const last = rows.sort((a, b) => b.sort_order - a.sort_order)[0]
  if (!last) return { title: null, lines: [] }
  const { data: lesson } = await supabase.from('lessons').select('content').eq('slug', last.slug).maybeSingle()
  const lines = ((lesson?.content as LessonContent | undefined)?.dialogue?.short ?? []).map((l) => l.ru).filter(Boolean).slice(0, 6)
  return { title: last.unit_title, lines }
}

function goalContext(goalId: string | null): GoalContext | undefined {
  const t = topicById(goalId)
  return t?.chatGoal ? { title: t.title, focus: t.chatGoal.focus, detect: t.chatGoal.detect } : undefined
}

async function createChatSession(supabase: SupabaseClient, userId: string, mode: SessionMode): Promise<string> {
  // Разговор по уроку: 3 слова задачи выбираются здесь, один раз на сессию.
  let taskWords: string[] | null = null
  if (mode.lessonSlug) {
    const { data: lesson } = await supabase.from('lessons').select('content').eq('slug', mode.lessonSlug).maybeSingle()
    if (!lesson) throw new Error('Урок не найден')
    const content = lesson.content as LessonContent
    taskWords = pickScenarioTaskWords(content.vocabulary ?? [], content.dialogue)
  }
  // Сцена цели: три задачи сцены — в task_words, отметки модели — в task_done.
  if (mode.learningGoal) taskWords = [...goalById(mode.learningGoal)!.scene.tasks]
  // Сцена платной ситуации «для жизни» — не в обход страницы чата.
  if (mode.scene && (await sceneGate(supabase, userId, mode.scene)).gate !== 'open') {
    throw new Error('Эта сцена открывается с подпиской Plus')
  }
  // Своя ситуация: сцена должна существовать и быть своей (RLS чужую не отдаст).
  if (mode.customSceneId) {
    const { data: cs } = await supabase.from('custom_scenes').select('id').eq('id', mode.customSceneId).maybeSingle()
    if (!cs) throw new Error('Ситуация не найдена')
  }
  const { data: session, error } = await supabase
    .from('chat_sessions')
    .insert({
      user_id: userId,
      scene: mode.scene,
      lesson_slug: mode.lessonSlug,
      task_words: taskWords,
      grammar_goal: mode.grammarGoal,
      learning_goal: mode.learningGoal,
      custom_scene_id: mode.customSceneId,
      persona: mode.persona,
    })
    .select('id')
    .single()
  if (error || !session) {
    throw new Error(error?.message ?? 'Не удалось создать сессию чата')
  }
  return session.id
}

// Ролевые сцены «к отпуску» и «для жизни» (lib/travel.ts SCENES): роль и
// цель — из реестра, словарь сцены — фразы урока ситуации (loadSceneContext).
// Финальная сцена цели: роль, задачи и опорные фразы из каталога целей.
function goalSceneContext(goalId: string | null, tasksDone: string[]): SceneContext | undefined {
  const g = goalById(goalId)
  if (!g) return undefined
  return {
    title: g.title,
    role: g.scene.role,
    goal: `${g.promise}.`,
    phrases: g.scene.phrases,
    learner: g.scene.learner,
    tasks: [...g.scene.tasks],
    tasksDone,
  }
}

// Сцена трека-разговорника («к отпуску», «для жизни») или «своя ситуация»
// (custom_scenes) — с характером собеседника из сессии.
async function loadSceneContext(
  supabase: SupabaseClient,
  info: { scene: RoleScene | null; customSceneId: string | null; persona: Persona | null },
): Promise<SceneContext | undefined> {
  if (info.scene) {
    const { data } = await supabase.from('lessons').select('content').eq('track', SCENES[info.scene].track).eq('content->>scene', info.scene).maybeSingle()
    return { ...sceneContextFrom(SCENES[info.scene], data?.content as LessonContent | undefined), persona: info.persona }
  }
  if (info.customSceneId) {
    const { data } = await supabase.from('custom_scenes').select('title, role, learner, goal, phrases').eq('id', info.customSceneId).maybeSingle()
    if (!data) return undefined
    const phrases = ((data.phrases as CustomScenePhrase[] | null) ?? []).map((p) => `${p.tr} — ${p.ru}`)
    return { title: data.title, role: data.role, learner: data.learner, goal: data.goal, phrases, persona: info.persona, custom: true }
  }
  return undefined
}

// Разговор по уроку: тема, слова и грамматика урока + слова задачи сессии.
async function loadLessonContext(
  supabase: SupabaseClient,
  lessonSlug: string | null,
  taskWords: string[] | null,
): Promise<LessonChatContext | undefined> {
  if (!lessonSlug) return undefined
  const { data } = await supabase.from('lessons').select('topic, content').eq('slug', lessonSlug).maybeSingle()
  if (!data) return undefined
  return lessonContextFrom(data.topic, data.content as LessonContent, taskWords ?? [])
}

// Режим сессии — из БД, а не от клиента: подменить роль или урок чужой сессии нельзя.
async function sessionInfo(
  supabase: SupabaseClient,
  sessionId: string,
): Promise<{
  scene: RoleScene | null
  lessonSlug: string | null
  taskWords: string[] | null
  grammarGoal: string | null
  learningGoal: string | null
  tasksDone: string[]
  customSceneId: string | null
  persona: Persona | null
  topics: string[]
}> {
  const { data } = await supabase
    .from('chat_sessions')
    .select('scene, lesson_slug, task_words, grammar_goal, learning_goal, task_done, custom_scene_id, persona, topics')
    .eq('id', sessionId)
    .maybeSingle()
  return {
    scene: isRoleScene(data?.scene) ? data.scene : null,
    lessonSlug: data?.lesson_slug ?? null,
    taskWords: data?.task_words ?? null,
    grammarGoal: topicById(data?.grammar_goal)?.chatGoal ? data!.grammar_goal : null,
    learningGoal: goalById(data?.learning_goal) ? (data!.learning_goal as string) : null,
    tasksDone: (data?.task_done as string[] | null) ?? [],
    customSceneId: (data?.custom_scene_id as string | null) ?? null,
    persona: isPersona(data?.persona) ? data!.persona : null,
    topics: (data?.topics as string[] | null) ?? [],
  }
}

function highestLevel(levels: string[]): CourseLevel {
  let best: CourseLevel = 'A1'
  for (const level of levels) {
    const idx = LEVEL_ORDER.indexOf(level as CourseLevel)
    if (idx > LEVEL_ORDER.indexOf(best)) best = LEVEL_ORDER[idx]
  }
  return best
}

async function loadLearnerProfile(
  supabase: SupabaseClient,
  userId: string,
): Promise<{ level: CourseLevel; knownWords: string[]; grammar: string[]; vocabIdByFold: Map<string, string> }> {
  const [{ data: vocabRows }, { data: levelRows }, { data: ownRows }] = await Promise.all([
    supabase.from('user_vocab_progress').select('vocab_id, vocabulary(turkish)').eq('user_id', userId),
    supabase
      .from('user_lesson_progress')
      .select('lessons(level, slug)')
      .eq('user_id', userId)
      .not('completed_at', 'is', null),
    // «Мои слова» — сам нашёл через «Как сказать?»: собеседник может
    // употреблять их в разговоре, и слово встречается уже в живом диалоге.
    supabase.from('user_words').select('turkish').eq('user_id', userId),
  ])

  // Сортировка — ради кэша контекста DeepSeek: он кэширует совпадающее НАЧАЛО
  // промпта (попадание в кэш ~в 50 раз дешевле), а список слов — самая
  // большая его часть. Без ORDER BY Postgres отдаёт строки в физическом
  // порядке, который меняется после каждого повторения (UPDATE переносит
  // строку), — и кэш промахивался почти всегда. С сортировкой список меняется
  // только после нового урока.
  const vocabList = (vocabRows ?? []) as unknown as { vocab_id: string; vocabulary: { turkish: string } | null }[]
  // Слово словаря по свёрнутой форме (trFold): для повтора слова после
  // исправления в чате (sendMessage). Только одиночные слова.
  const vocabIdByFold = new Map<string, string>()
  for (const r of vocabList) {
    const t = r.vocabulary?.turkish.trim()
    if (t && !/\s/.test(t)) vocabIdByFold.set(trFold(t), r.vocab_id)
  }
  const knownWords = [
    ...new Set([
      ...vocabList
        .map((r) => r.vocabulary?.turkish)
        .filter((w): w is string => !!w),
      ...((ownRows ?? []) as { turkish: string }[]).map((r) => r.turkish),
    ]),
  ].sort((a, b) => (a < b ? -1 : a > b ? 1 : 0))

  const lessonRows = (levelRows ?? []) as unknown as { lessons: { level: string; slug: string } | null }[]
  const levels = lessonRows.map((r) => r.lessons?.level).filter((l): l is string => !!l)
  // Грамматика только из пройденных уроков (lib/grammar/chat-grammar.ts).
  const grammar = passedGrammarNames(passedRules(lessonRows.map((r) => r.lessons?.slug).filter((s): s is string => !!s)))

  return { level: highestLevel(levels), knownWords, grammar, vocabIdByFold }
}

// Слова словаря, в которых ученик ошибся: слова исправленной фразы, которых
// нет в его реплике (сравнение по trFold, то есть без учёта регистра и
// диакритики: ошибка в диакритике считается ошибкой в этом слове не здесь, а
// в тренажёре). Не больше трёх.
function missedVocabIds(userText: string, corrected: string, vocabIdByFold: Map<string, string>): string[] {
  const tokens = (t: string) => trFold(t).split(/[^\p{L}]+/u).filter(Boolean)
  const said = new Set(tokens(userText))
  const out: string[] = []
  for (const w of tokens(corrected)) {
    if (said.has(w)) continue
    const id = vocabIdByFold.get(w)
    if (id && !out.includes(id)) out.push(id)
    if (out.length >= 3) break
  }
  return out
}

async function requireUserId(supabase: SupabaseClient): Promise<string> {
  const { data: claimsData, error: claimsError } = await supabase.auth.getClaims()
  if (claimsError || !claimsData?.claims) {
    throw new Error('Unauthorized')
  }
  return claimsData.claims.sub
}

// Расход недельного лимита реплик — для полосы в шапке чата (chat/page.tsx →
// ai-chat.tsx). Страница cookie устройства не создаёт — только читает.
export async function getChatUsage(supabase: SupabaseClient, userId: string): Promise<AiUsage> {
  return getAiUsage(supabase, userId, 'chat', await deviceId(false))
}

const CHAT_LIMIT_ERROR = (limit: number) =>
  `На этой неделе все реплики с AI потрачены, лимит ${limit}. Новые появятся в понедельник, а уроки, повторение и "Турецкий к отпуску" можно проходить и сейчас.`

// Лимит — обычный результат, а не throw: текст ошибки server action в
// продакшене Next.js до клиента не доходит. limited: true — клиент показывает
// LimitNotice с предложением подписки, а не просто текст ошибки.
export type ChatLimitResult = { ok: false; error: string; usage: AiUsage; limited?: true }

// Первая реплика: из кэша (сцены и разговоры по урокам — одинаковые для всех
// учеников, lib/ai-cache.ts) или новая. Пока вариантов на ключ меньше
// OPENING_VARIANTS — генерируем и докладываем в кэш; потом — случайная готовая.
async function openingFor(
  userId: string,
  device: string | null,
  supabase: SupabaseClient,
  info: Awaited<ReturnType<typeof sessionInfo>>,
  params: Parameters<typeof getOpeningMessage>[0],
): Promise<OpeningMessage | null> {
  // Сцена «к отпуску» с характером — свой ключ кэша (реплика зависит от
  // характера). Своя ситуация — личная, в общий кэш не идёт (base null).
  const base = info.scene
    ? sceneOpeningBase(info.persona && info.persona !== DEFAULT_PERSONA ? `${info.scene}:${info.persona}` : info.scene)
    : info.learningGoal
      ? sceneOpeningBase(`skill-${info.learningGoal}`)
      : info.lessonSlug && info.taskWords?.[0]
      ? lessonOpeningBase(info.lessonSlug, info.taskWords[0])
      : null
  const admin = createAdminClient()
  // Варианты ответа с переводом появились 10.10.2026: записи кэша без перевода
  // не берём, кэш заполняется новыми (ключи с "t", чтобы не совпасть со старыми).
  const cached: OpeningMessage[] = base
    ? (((await admin.from('chat_opening_cache').select('opening').eq('base', base)).data ?? []).map((r) => r.opening) as OpeningMessage[])
        .map((o) => ({ ...o, suggestedReplies: normalizeSuggestions(o.suggestedReplies) }))
        .filter((o) => o.suggestedReplies.length > 0 && o.suggestedReplies.every((s) => s.ru))
    : []
  const pickCached = () => cached[Math.floor(Math.random() * cached.length)]
  if (cached.length > 0 && cached.length >= (info.scene || info.learningGoal ? OPENING_VARIANTS : LESSON_OPENING_VARIANTS)) return pickCached()

  // Готовых мало — нужен запрос к AI; у бесплатного тарифа на это тихий потолок.
  const openingUsage = await getAiUsage(supabase, userId, 'opening', device)
  if (usageExhausted(openingUsage)) return cached.length > 0 ? pickCached() : null

  const opening = await getOpeningMessage({ ...params, avoid: cached.map((o) => o.reply) })
  await recordAiUsage(userId, 'opening', device)
  if (base) {
    await admin
      .from('chat_opening_cache')
      .upsert({ key: `${base}#t${cached.length + 1}`, base, opening }, { onConflict: 'key', ignoreDuplicates: true })
  }
  return opening
}

// Открывающая реплика — вызывается один раз при первом заходе ученика в
// /chat, до того как он написал хоть слово. Решает проблему первых 15-20
// уроков: словаря почти нет, начинать разговор первым ученику нечем.
// existingSessionId позволяет повторить попытку, если предыдущий вызов создал
// сессию, но упал до записи сообщения (иначе плодились бы пустые сессии).
export async function startConversation(
  existingSessionId: string | null,
  requested: ChatModeRequest = {},
): Promise<
  | { ok: true; sessionId: string; assistantMessage: ChatMessage; usage: AiUsage; taskWords: string[] | null }
  | ChatLimitResult
> {
  const supabase = await createClient()
  const userId = await requireUserId(supabase)
  const device = await deviceId(true)

  // Реплики на неделю кончились — ответить собеседнику ученик всё равно не
  // сможет, так что и первую реплику (запрос к AI) не тратим.
  const usage = await getAiUsage(supabase, userId, 'chat', device)
  if (usageExhausted(usage)) return { ok: false, error: CHAT_LIMIT_ERROR(usage.limit!), usage, limited: true }

  const activeSessionId = existingSessionId ?? (await createChatSession(supabase, userId, parseMode(requested)))
  const info = await sessionInfo(supabase, activeSessionId)
  const [{ level, knownWords, grammar }, roleSceneContext, lessonContext] = await Promise.all([
    loadLearnerProfile(supabase, userId),
    loadSceneContext(supabase, info),
    loadLessonContext(supabase, info.lessonSlug, info.taskWords),
  ])
  const sceneContext = roleSceneContext ?? goalSceneContext(info.learningGoal, info.tasksDone)

  const free = isFreeChat(info)
  const opening = await openingFor(userId, device, supabase, info, {
    level,
    knownWords,
    grammar: sceneContext ? undefined : grammar,
    scene: sceneContext,
    lesson: lessonContext,
    goal: goalContext(info.grammarGoal),
    freeTopic: free ? await loadFreeTopic(supabase, userId) : undefined,
  })
  if (!opening) {
    return {
      ok: false,
      error: 'Слишком много новых разговоров на этой неделе - продолжите текущий или загляните в понедельник.',
      usage,
    }
  }

  const { data: inserted, error: insertError } = await supabase
    .from('chat_messages')
    .insert({
      session_id: activeSessionId,
      role: 'assistant',
      content: opening.reply,
      translation_ru: opening.replyTranslationRu,
      corrections: null,
      suggested_replies: opening.suggestedReplies,
    })
    .select('id, role, content, translation_ru, corrections, suggested_replies, created_at')
    .single()

  if (insertError || !inserted) {
    throw new Error(insertError?.message ?? 'Не удалось сохранить сообщение')
  }

  // Обычный разговор: задачи от модели - в task_words, отметки - в task_done.
  // Сбой записи не мешает разговору, он просто идёт без задач.
  let taskWords = info.taskWords
  if (free && (opening.tasks?.length || opening.topics?.length)) {
    const { error: taskErr } = await supabase
      .from('chat_sessions')
      .update({
        ...(opening.tasks?.length ? { task_words: opening.tasks, task_done: [] } : {}),
        ...(opening.topics?.length ? { topics: opening.topics } : {}),
      })
      .eq('id', activeSessionId)
    if (taskErr) console.warn('[free chat] задачи не записаны:', taskErr.message)
    else if (opening.tasks?.length) taskWords = opening.tasks
  }

  return {
    ok: true,
    sessionId: activeSessionId,
    assistantMessage: {
      id: inserted.id,
      role: 'assistant',
      content: inserted.content,
      translationRu: inserted.translation_ru,
      correction: null,
      suggestedReplies: normalizeSuggestions(inserted.suggested_replies),
      createdAt: inserted.created_at,
    },
    usage,
    taskWords,
  }
}

export async function sendChatMessage(
  sessionId: string | null,
  text: string,
  requested: ChatModeRequest = {},
): Promise<
  | { ok: true; sessionId: string; userMessage: ChatMessage; assistantMessage: ChatMessage; usage: AiUsage; tasksDone?: string[]; ended: boolean }
  | ChatLimitResult
> {
  const trimmed = text.trim()
  if (!trimmed) {
    throw new Error('Сообщение пустое')
  }

  const supabase = await createClient()
  const userId = await requireUserId(supabase)
  const device = await deviceId(true)

  const usageBeforeSend = await getAiUsage(supabase, userId, 'chat', device)
  if (usageExhausted(usageBeforeSend)) {
    return { ok: false, error: CHAT_LIMIT_ERROR(usageBeforeSend.limit!), usage: usageBeforeSend, limited: true }
  }

  const activeSessionId = sessionId ?? (await createChatSession(supabase, userId, parseMode(requested)))
  const info = await sessionInfo(supabase, activeSessionId)
  const [roleSceneContext, lessonContext] = await Promise.all([
    loadSceneContext(supabase, info),
    loadLessonContext(supabase, info.lessonSlug, info.taskWords),
  ])
  const sceneContext = roleSceneContext ?? goalSceneContext(info.learningGoal, info.tasksDone)

  const [{ data: historyRows }, { level, knownWords, grammar, vocabIdByFold }] = await Promise.all([
    supabase
      .from('chat_messages')
      .select('role, content')
      .eq('session_id', activeSessionId)
      .order('created_at', { ascending: false })
      // Реплика ученика и ответ AI пишутся одним insert и получают одинаковый
      // created_at; без второго ключа порядок пары случайный, и модель видела
      // свой вопрос ПОСЛЕ ответа ученика и задавала его снова (10.10.2026).
      // В паре ответ AI всегда позже: при убывании assistant идёт первым.
      .order('role', { ascending: true })
      .limit(20),
    loadLearnerProfile(supabase, userId),
  ])

  // Последние 20 сообщений, но в хронологическом порядке для модели —
  // .limit() на убывающей сортировке берёт САМЫЕ НОВЫЕ, reverse() возвращает
  // их в порядок диалога.
  const history: ChatHistoryItem[] = (historyRows ?? [])
    .slice()
    .reverse()
    .map((m) => ({ role: m.role as 'user' | 'assistant', content: m.content }))

  // Разговор по уроку: какие слова задачи уже прозвучали (с текущей репликой) —
  // чтобы AI вёл к оставшимся и сам завершил разговор, когда задача выполнена.
  if (lessonContext) {
    const userTexts = [...history.filter((m) => m.role === 'user').map((m) => m.content), trimmed]
    lessonContext.usedWords = [...usedTaskWords(lessonContext.taskWords, userTexts)]
  }

  const goal = goalContext(info.grammarGoal)
  const freeTasks: FreeTasks | undefined =
    isFreeChat(info) && info.taskWords?.length ? { tasks: info.taskWords, tasksDone: info.tasksDone } : undefined
  const freeTopicPlan: FreeTopicPlan | undefined =
    isFreeChat(info) && info.topics.length ? { topics: info.topics, studentReplies: history.filter((m) => m.role === 'user').length } : undefined
  const tutorReply = await getTutorReply({ level, knownWords, grammar: sceneContext ? undefined : grammar, history, userMessage: trimmed, scene: sceneContext, lesson: lessonContext, goal, freeTasks, freeTopicPlan })

  const { data: inserted, error: insertError } = await supabase
    .from('chat_messages')
    .insert([
      {
        session_id: activeSessionId,
        role: 'user',
        content: trimmed,
        translation_ru: tutorReply.studentTranslationRu,
        corrections: tutorReply.correction,
        goal_use: goal ? tutorReply.goalUse : null,
      },
      {
        session_id: activeSessionId,
        role: 'assistant',
        content: tutorReply.reply,
        translation_ru: tutorReply.replyTranslationRu,
        corrections: null,
        suggested_replies: tutorReply.suggestedReplies,
      },
    ])
    .select('id, role, content, translation_ru, corrections, suggested_replies, goal_use, created_at')

  if (insertError || !inserted || inserted.length !== 2) {
    throw new Error(insertError?.message ?? 'Не удалось сохранить сообщения')
  }
  // Засчитываем только реплику, на которую пришёл ответ.
  await recordAiUsage(userId, 'chat', device)

  // Сцена цели и обычный разговор с задачами: отметки копятся в сессии
  // (объединение, без повторов).
  let tasksDoneNow: string[] | undefined
  if (info.learningGoal || freeTasks) {
    tasksDoneNow = [...new Set([...info.tasksDone, ...(tutorReply.tasksDone ?? [])])]
    if (tasksDoneNow.length !== info.tasksDone.length) {
      const { error: taskErr } = await supabase.from('chat_sessions').update({ task_done: tasksDoneNow }).eq('id', activeSessionId)
      if (taskErr) console.warn('[goal scene] task_done не записан:', taskErr.message)
    }
  }

  // Ошибка, помеченная темой каталога, — в «Слабые места» тренажёра грамматики.
  // Сбой записи не мешает разговору.
  if (tutorReply.correction?.topic) {
    const { error: mistakeError } = await supabase.from('grammar_mistakes').insert({
      user_id: userId,
      topic_id: tutorReply.correction.topic,
      source: 'chat',
      wrong: trimmed.slice(0, 200),
      correct: tutorReply.correction.corrected.slice(0, 200),
      explanation: tutorReply.correction.explanationRu.slice(0, 500),
    })
    if (mistakeError) console.warn('[grammar_mistakes] не записано:', mistakeError.message)
  } else if (tutorReply.correction) {
    // Исправление без темы каталога, но с конкретным словом из словаря ученика:
    // слову ставится повтор через 4 часа, как пропущенному в уроке
    // (srs_interval 0, next_review_at now() + 4h). Сбой записи не мешает разговору.
    const ids = missedVocabIds(trimmed, tutorReply.correction.corrected, vocabIdByFold)
    if (ids.length > 0) {
      const { error: srsError } = await supabase
        .from('user_vocab_progress')
        .update({ srs_interval: 0, next_review_at: new Date(Date.now() + 4 * 3600 * 1000).toISOString(), updated_at: new Date().toISOString() })
        .eq('user_id', userId)
        .in('vocab_id', ids)
      if (srsError) console.warn('[chat] повтор слова не поставлен:', srsError.message)
    }
  }

  const userRow = inserted.find((r) => r.role === 'user')!
  const assistantRow = inserted.find((r) => r.role === 'assistant')!

  return {
    ok: true,
    sessionId: activeSessionId,
    userMessage: {
      id: userRow.id,
      role: 'user',
      content: userRow.content,
      translationRu: userRow.translation_ru,
      correction: userRow.corrections,
      goalUse: userRow.goal_use ?? null,
      suggestedReplies: [],
      createdAt: userRow.created_at,
    },
    assistantMessage: {
      id: assistantRow.id,
      role: 'assistant',
      content: assistantRow.content,
      translationRu: assistantRow.translation_ru,
      correction: null,
      suggestedReplies: normalizeSuggestions(assistantRow.suggested_replies),
      createdAt: assistantRow.created_at,
    },
    usage: afterOneMore(usageBeforeSend),
    tasksDone: tasksDoneNow,
    ended: tutorReply.farewell && (tutorReply.studentFarewell || allTasksDone(lessonContext, info.taskWords, tasksDoneNow)),
  }
}

// Разговор окончен, когда собеседник попрощался и либо задачи сделаны, либо
// ученик попрощался первым: прощание AI посреди сцены (таксист довёз раньше
// задач) разговор не обрывает. Без задач хватает прощания. Тогда клиент сам открывает итог, и ученик не
// ходит по кругу «Görüşürüz» - «Bravo, görüşürüz!» (10.10.2026).
function allTasksDone(lesson: LessonChatContext | undefined, taskWords: string[] | null, tasksDone: string[] | undefined): boolean {
  if (lesson) return (lesson.usedWords?.length ?? 0) >= lesson.taskWords.length
  if (tasksDone && taskWords?.length) return taskWords.every((t) => tasksDone.includes(t))
  return true
}

// Финальная сцена цели пройдена (все задачи отмечены) — БД перепроверяет по
// task_done и начисляет бонус как за задачу разговора, один раз на цель
// (complete_goal_scene, миграция 20260918110000_goal_scenes.sql).
export async function completeGoalScene(sessionId: string, timeZone: string): Promise<{ already: boolean; xp?: number }> {
  const supabase = await createClient()
  await requireUserId(supabase)
  const { data, error } = await supabase.rpc('complete_goal_scene', { p_session_id: sessionId, p_timezone: timeZone })
  if (error) throw new Error(error.message)
  const r = (data ?? {}) as { already?: boolean; xp_gained?: number; gain?: number }
  return { already: !!r.already, xp: r.xp_gained ?? r.gain }
}

// Задача разговора по уроку выполнена (клиент видит все 3 слова в репликах) —
// БД сама перепроверяет и начисляет бонус, один раз на урок
// (complete_chat_task, миграция 20260911180000_lesson_chat.sql).
export async function completeLessonChatTask(
  sessionId: string,
  timeZone: string | null,
): Promise<{ done: boolean; already?: boolean; xpGained?: number }> {
  const supabase = await createClient()
  await requireUserId(supabase)
  const { data, error } = await supabase.rpc('complete_chat_task', { p_session_id: sessionId, p_timezone: timeZone })
  if (error) throw new Error(error.message)
  const res = (data ?? {}) as { done?: boolean; already?: boolean; xp_gained?: number }
  return { done: !!res.done, already: res.already, xpGained: res.xp_gained }
}

// Режим «Спросить» (.scratch/voice-ai-tutor/issues/02) — вопрос ученика о
// языке, а не реплика ролевого диалога. По решению пользователя история v1
// нигде не сохраняется (ни в БД, ни между перезагрузками страницы) — history
// приходит из client-side состояния компонента. Лимит — недельный, по журналу
// ai_usage (там только факт вопроса, без текста).
export type AskTutorResult =
  | { ok: true; answer: TutorQaAnswer; usage: AiUsage }
  | { ok: false; error: string; usage: AiUsage; limited?: true }

export async function askTutorQuestion(question: string, history: QaHistoryItem[]): Promise<AskTutorResult> {
  const trimmed = question.trim()
  if (!trimmed) {
    throw new Error('Вопрос пустой')
  }

  const supabase = await createClient()
  const userId = await requireUserId(supabase)
  const device = await deviceId(true)

  const usage = await getAiUsage(supabase, userId, 'qa', device)
  if (usageExhausted(usage)) {
    return {
      ok: false,
      error: `Лимит вопросов репетитору на эту неделю исчерпан (${usage.limit}). Спросить снова можно будет в понедельник.`,
      usage,
      limited: true,
    }
  }

  const { level, knownWords } = await loadLearnerProfile(supabase, userId)

  // Вопросы о жизни в Турции (решение 2026-09-18): Plus/Pro — без отдельного
  // ограничения (getAiUsage вернёт limit: null), Free — два пробных в неделю.
  const turkeyUsage = await getAiUsage(supabase, userId, 'qa_turkey', device)
  const turkeyAllowed = !usageExhausted(turkeyUsage)

  // Общий кэш ответов: одинаковый вопрос одного уровня — готовый ответ, без
  // запроса к DeepSeek. Только первый вопрос разговора: уточнение («а подробнее?»)
  // без предыдущих вопросов бессмысленно. Ответ «о Турции нельзя» не кэшируем:
  // он зависит от тарифа, а не от вопроса.
  const key = history.length === 0 && turkeyAllowed ? qaCacheKey(level, trimmed) : null
  const admin = createAdminClient()
  let answer: TutorQaAnswer | null = null
  if (key) {
    const { data } = await admin.from('qa_answer_cache').select('answer').eq('key', key).maybeSingle()
    answer = (data?.answer as TutorQaAnswer | undefined) ?? null
  }
  if (!answer) {
    answer = await getQuestionAnswer({ level, knownWords, question: trimmed, history, turkeyAllowed })
    if (key && answer.topic !== 'other') {
      await admin
        .from('qa_answer_cache')
        .upsert({ key, level, question: trimmed, answer }, { onConflict: 'key', ignoreDuplicates: true })
    }
  }

  // Засчитываем только отвеченный вопрос (и из кэша тоже — для ученика это
  // обычный вопрос): упавший запрос к AI лимит не тратит.
  await recordAiUsage(userId, 'qa', device)
  // Пробный вопрос о Турции у Free расходуется только когда на него ответили по существу.
  if (answer.topic === 'turkey' && turkeyAllowed) await recordAiUsage(userId, 'qa_turkey', device)

  return { ok: true, answer, usage: afterOneMore(usage) }
}

// «Проверь моё сообщение» (lib/deepseek.ts getMessageCheck): тот же недельный
// лимит, что у вопросов репетитору ('qa'), без кэша — тексты личные и разные.
export type CheckMessageResult =
  | { ok: true; check: MessageCheck; usage: AiUsage }
  | { ok: false; error: string; usage: AiUsage; limited?: true }

export async function checkMyMessage(text: string): Promise<CheckMessageResult> {
  const trimmed = text.trim().slice(0, 1200)
  if (!trimmed) throw new Error('Текст пустой')
  const supabase = await createClient()
  const userId = await requireUserId(supabase)
  const device = await deviceId(true)
  const usage = await getAiUsage(supabase, userId, 'qa', device)
  if (usageExhausted(usage)) {
    return {
      ok: false,
      error: `Проверка сообщений делит лимит с вопросами репетитору (${usage.limit} в неделю), и на этой неделе он кончился. Счётчик сбросится в понедельник.`,
      usage,
      limited: true,
    }
  }
  const { level, knownWords } = await loadLearnerProfile(supabase, userId)
  const check = await getMessageCheck({ level, knownWords, text: trimmed })
  await recordAiUsage(userId, 'qa', device)
  return { ok: true, check, usage: afterOneMore(usage) }
}

// ── «Своя ситуация» (.scratch/custom-scene/01) ────────────────────────────────
// Ученик описывает ситуацию словами, DeepSeek собирает сцену (роль, цель,
// опорные фразы), она сохраняется в custom_scenes; дальше — /chat?custom=<id>.
// Свой недельный лимит 'custom_scene' (lib/chat.ts): считается только
// успешно созданная сцена, отказ модели («не подходит») лимит не тратит.
export type CreateCustomSceneResult =
  | { ok: true; id: string; usage: AiUsage }
  | { ok: false; error: string; usage: AiUsage; limited?: true }



export async function createCustomScene(prompt: string, requestedPersona: string | null): Promise<CreateCustomSceneResult> {
  const trimmed = prompt.replace(/\s+/g, ' ').trim().slice(0, CUSTOM_SCENE_PROMPT_MAX)
  if (trimmed.length < 3) throw new Error('Опишите ситуацию хотя бы парой слов')
  const persona = personaOrDefault(requestedPersona)

  const supabase = await createClient()
  const userId = await requireUserId(supabase)
  const device = await deviceId(true)

  const usage = await getAiUsage(supabase, userId, 'custom_scene', device)
  if (usageExhausted(usage)) {
    return {
      ok: false,
      error: `Больше ${usage.limit} новых ситуаций за неделю создать нельзя. Старые можно играть сколько угодно, а новую придумаете в понедельник.`,
      usage,
      limited: true,
    }
  }

  const { level, knownWords } = await loadLearnerProfile(supabase, userId)
  const result = await getCustomScene({ level, knownWords, prompt: trimmed, persona })
  if (!result.ok) return { ok: false, error: result.reason, usage }

  const { data: inserted, error } = await supabase
    .from('custom_scenes')
    .insert({
      user_id: userId,
      prompt: trimmed,
      emoji: result.scene.emoji,
      title: result.scene.title,
      role: result.scene.role,
      learner: result.scene.learner,
      goal: result.scene.goal,
      phrases: result.scene.phrases,
    })
    .select('id')
    .single()
  if (error || !inserted) throw new Error(error?.message ?? 'Не удалось сохранить ситуацию')

  await recordAiUsage(userId, 'custom_scene', device)
  return { ok: true, id: inserted.id as string, usage: afterOneMore(usage) }
}

// Удаление своей ситуации (RLS — только свои строки); разговоры по ней
// уходят каскадом.
export async function deleteCustomScene(id: string): Promise<void> {
  if (!UUID_RE.test(id)) throw new Error('Некорректный id')
  const supabase = await createClient()
  await requireUserId(supabase)
  const { error } = await supabase.from('custom_scenes').delete().eq('id', id)
  if (error) throw new Error(error.message)
}
